import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, Home, Users, Settings, LogOut, FileText, Calendar, ClipboardList, Menu, X, Info, ChevronDown, ChevronRight, Briefcase, CheckSquare, UserCircle, Zap, Search, QrCode, PenSquare, Upload, MessageSquare } from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';
import { useIsNativeApp } from '../hooks/useIsNativeApp';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { agent, logout } = useAuth();
  const { colors } = useTheme();
  const isNativeApp = useIsNativeApp();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aboutMenuOpen, setAboutMenuOpen] = useState(false);
  const [showQuickSignIn, setShowQuickSignIn] = useState(false);
  const [enabledPages, setEnabledPages] = useState({
    about: true,
    contact: true,
    career: true,
    faq: true
  });

  useEffect(() => {
    // Fetch site settings to check which pages are enabled
    const fetchEnabledPages = async () => {
      try {
        const response = await axios.get(`${API}/site-settings`);
        if (response.data.pages_enabled) {
          const newEnabledPages = {
            about: response.data.pages_enabled.about !== false,
            contact: response.data.pages_enabled.contact !== false,
            career: response.data.pages_enabled.career !== false,
            faq: response.data.pages_enabled.faq !== false
          };
          setEnabledPages(newEnabledPages);
        }
      } catch (error) {
        console.error('[Layout] Failed to fetch site settings:', error);
        // Keep default values (all enabled) on error
      }
    };

    // Fetch admin settings to check if Quick Sign-In is enabled
    const fetchQuickSignInSetting = async () => {
      try {
        const response = await axios.get(`${API}/app-settings/show_quick_signin`);
        setShowQuickSignIn(response.data?.value || false);
      } catch (error) {
        console.error('[Layout] Failed to fetch Quick Sign-In setting:', error);
        setShowQuickSignIn(false);
      }
    };

    fetchEnabledPages();
    fetchQuickSignInSetting();
  }, []);

  const allAboutSubItems = [
    { label: 'About Us', path: '/about', key: 'about' },
    { label: 'Contact', path: '/contact', key: 'contact' },
    { label: 'Careers', path: '/career', key: 'career' },
    { label: 'FAQ', path: '/faq', key: 'faq' },
  ];

  // Filter to only show enabled pages
  const aboutSubItems = allAboutSubItems.filter(item => enabledPages[item.key]);

  const isBrokerApproved = agent?.role !== 'broker' || agent?.status === 'approved' || agent?.status === 'active';
  const isAgentActive = agent?.role !== 'agent' || !['pending'].includes(agent?.status);

  const navItems = agent?.role === 'broker' ? [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/broker' },
    { label: 'Properties', icon: Home, path: '/broker/properties' },
    { label: 'Leads', icon: Users, path: '/leads' },
    { label: 'Assigned Leads', icon: CheckSquare, path: '/assigned-leads' },
    { label: 'Provider Pool', icon: Users, path: '/provider-pool' },
    { label: 'Connect', icon: MessageSquare, path: '/connect' },
    { label: 'Public Profile', icon: UserCircle, path: '/public-profile' },
    { label: 'Reviews', icon: FileText, path: '/reviews' },
    ...(isBrokerApproved ? [{ label: 'QR Codes', icon: QrCode, path: '/qr-codes' }] : []),
    { label: 'Jobs', icon: PenSquare, path: '/post-jobs' },
    { label: 'Search', icon: Search, path: '/search' },
    { label: 'Settings', icon: Settings, path: '/settings' },
    { label: 'Quick Sign-In', icon: Zap, path: '/broker/quick-signin' },
    { label: 'Service Providers', icon: Briefcase, path: '/service-providers' },
    { label: 'File Upload', icon: Upload, path: '/file-upload' },
    { label: 'Templates', icon: FileText, path: '/templates' },
  ] : [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    ...(isAgentActive ? [{ label: 'Properties', icon: Home, path: '/agent/properties' }] : []),
    { label: 'Assignments', icon: ClipboardList, path: '/agent/assignments' },
    { label: 'Leads', icon: Users, path: '/leads' },
    { label: 'Assigned Leads', icon: CheckSquare, path: '/assigned-leads' },
    { label: 'Provider Pool', icon: Users, path: '/provider-pool' },
    { label: 'Connect', icon: MessageSquare, path: '/connect' },
    { label: 'Public Profile', icon: UserCircle, path: '/public-profile' },
    { label: 'Reviews', icon: FileText, path: '/reviews' },
    { label: 'QR Codes', icon: QrCode, path: '/qr-codes' },
    { label: 'Jobs', icon: PenSquare, path: '/post-jobs' },
    { label: 'Search', icon: Search, path: '/search' },
    { label: 'Settings', icon: Settings, path: '/settings' },
    ...(showQuickSignIn ? [{ label: 'Quick Sign-In', icon: Zap, path: '/agent/quick-signin' }] : []),
    { label: 'Service Providers', icon: Briefcase, path: '/service-providers' },
    { label: 'File Upload', icon: Upload, path: '/file-upload' },
    { label: 'Templates', icon: FileText, path: '/templates' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.bg }}>
      {/* Mobile Header */}

      <header className="lg:hidden fixed top-0 inset-x-0 bg-white border-b border-stone-200 px-4 py-3 flex items-center justify-between z-50">
        {!isNativeApp && (
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 -ml-2 rounded-lg hover:bg-stone-100"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        )}
        <h1 className="text-xl font-bold" style={{ fontFamily: '"Playfair Display", serif', color: colors.primary }}>
          Open House
        </h1>
        <NotificationBell />
      </header>


      {/* Mobile Sidebar Overlay */}
      {!isNativeApp && mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      {!isNativeApp && (
        <aside className={`lg:hidden fixed inset-y-0 left-0 w-64 bg-white border-r border-stone-200 z-50 transform transition-transform duration-300 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}>
          <div className="flex flex-col h-full pt-20 pb-4">
            <div className="px-6 mb-6">
              <p className="text-sm text-stone-500">{agent?.name}</p>
              <p className="text-xs text-stone-400 mt-1">{agent?.email}</p>
            </div>

            <nav className="flex-1 px-4 space-y-2 overflow-y-auto">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={`mobile-${item.label}-${item.path}`}
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive
                        ? 'text-white'
                        : 'text-stone-600 hover:bg-stone-100'
                      }`}
                    style={isActive ? { backgroundColor: colors.primary } : {}}
                  >
                    <Icon size={20} strokeWidth={1.5} className="flex-shrink-0" />
                    <span className="font-medium text-sm sm:text-base truncate">{item.label}</span>
                  </button>
                );
              })}

              {/* About Menu with Sub-items - Only show if there are enabled pages */}
              {aboutSubItems.length > 0 && (
                <div>
                  <button
                    onClick={() => setAboutMenuOpen(!aboutMenuOpen)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${aboutSubItems.some(sub => location.pathname === sub.path)
                        ? 'text-white'
                        : 'text-stone-600 hover:bg-stone-100'
                      }`}
                    style={aboutSubItems.some(sub => location.pathname === sub.path) ? { backgroundColor: colors.primary } : {}}
                  >
                    <Info size={20} strokeWidth={1.5} />
                    <span className="font-medium flex-1 text-left">About</span>
                    {aboutMenuOpen ? (
                      <ChevronDown size={16} strokeWidth={1.5} />
                    ) : (
                      <ChevronRight size={16} strokeWidth={1.5} />
                    )}
                  </button>

                  {aboutMenuOpen && (
                    <div className="ml-8 mt-1 space-y-1">
                      {aboutSubItems.map((subItem) => {
                        const isActive = location.pathname === subItem.path;
                        return (
                          <button
                            key={`mobile-sub-${subItem.path}`}
                            onClick={() => {
                              navigate(subItem.path);
                              setMobileMenuOpen(false);
                            }}
                            className={`w-full flex items-center px-4 py-2 rounded-lg text-sm transition-colors ${isActive
                                ? 'font-medium'
                                : 'text-stone-600 hover:bg-stone-100'
                              }`}
                            style={isActive ? { backgroundColor: `${colors.primary}1A`, color: colors.primary } : {}}
                          >
                            {subItem.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </nav>

            <div className="px-4 mt-auto">
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="w-full justify-start gap-3 text-stone-600 hover:bg-stone-100"
              >
                <LogOut size={20} strokeWidth={1.5} />
                <span>Logout</span>
              </Button>
            </div>
          </div>
        </aside>
      )}

      {/* Desktop Sidebar */}
      {!isNativeApp && (
        <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
          <div className="flex flex-col flex-grow pt-8 pb-4 overflow-y-auto bg-white border-r border-stone-200">
            <div className="px-6 mb-8 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold" style={{ fontFamily: '"Playfair Display", serif', color: colors.primary }}>Open House</h1>
                <p className="text-sm text-stone-500 mt-1">{agent?.name}</p>
              </div>
              <NotificationBell />
            </div>

            <nav className="flex-1 px-4 space-y-2 overflow-y-auto">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={`${item.label}-${item.path}`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive
                        ? 'text-white'
                        : 'text-stone-600 hover:bg-stone-100'
                      }`}
                    style={isActive ? { backgroundColor: colors.primary } : {}}
                  >
                    <Icon size={20} strokeWidth={1.5} />
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}

              {/* About Menu with Sub-items - Only show if there are enabled pages */}
              {aboutSubItems.length > 0 && (
                <div>
                  <button
                    onClick={() => setAboutMenuOpen(!aboutMenuOpen)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${aboutSubItems.some(sub => location.pathname === sub.path)
                        ? 'text-white'
                        : 'text-stone-600 hover:bg-stone-100'
                      }`}
                    style={aboutSubItems.some(sub => location.pathname === sub.path) ? { backgroundColor: colors.primary } : {}}
                  >
                    <Info size={20} strokeWidth={1.5} />
                    <span className="font-medium flex-1 text-left">About</span>
                    {aboutMenuOpen ? (
                      <ChevronDown size={16} strokeWidth={1.5} />
                    ) : (
                      <ChevronRight size={16} strokeWidth={1.5} />
                    )}
                  </button>

                  {aboutMenuOpen && (
                    <div className="ml-8 mt-1 space-y-1">
                      {aboutSubItems.map((subItem) => {
                        const isActive = location.pathname === subItem.path;
                        return (
                          <button
                            key={`sub-${subItem.path}`}
                            onClick={() => navigate(subItem.path)}
                            className={`w-full flex items-center px-4 py-2 rounded-lg text-sm transition-colors ${isActive
                                ? 'font-medium'
                                : 'text-stone-600 hover:bg-stone-100'
                              }`}
                            style={isActive ? { backgroundColor: `${colors.primary}1A`, color: colors.primary } : {}}
                          >
                            {subItem.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </nav>

            <div className="px-4 mt-auto">
              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="ghost"
                className="w-full justify-start gap-3 text-stone-600 hover:bg-stone-100"
              >
                <LogOut size={20} strokeWidth={1.5} />
                <span>Logout</span>
              </Button>
            </div>
          </div>
        </aside>
      )}

      {/*6. Main Content - Adjust padding to 0 if it's the native app */}
      <div className="lg:pl-64">
        <main className="pt-16 lg:pt-0 pb-4 lg:pb-0 p-4 md:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
